import cloneDeep from 'lodash.clonedeep';
import { setQueryString, getQueryString } from '@/utils/UrlUtils';
import { isEmptyString } from '@/utils/DisplayUtils';

const INITIAL_STATE = {
  formResponse: {
    step1: {
      member: '',
      modality: '',
    },
    step2: {
      selectedProvider: '',
      searchType: '',
      providerNumber: '',
      providerSurname: '',
      suburb: '',
      state: '',
    },
    step3: {
      selectedProvider: '',
    },
    step4: {
      fileUploaded: false,
      fileRemoved: false,
      claimLines: [
        {
          serviceDate: '',
          serviceId: '',
          flexCode: '',
          toothId: '',
          providerFee: '',
          uploadedFiles: {
            size: 0,
            files: [],
            fileImgs: [],
            fileNames: [],
            fileTypes: [],
            fileInvalid: false,
          },
        },
      ],
    },
    step5: {
      member: '',
    },
  },
  currentPage: 1,
  ref: {
    content: [],
    memberNumber: '',
    members: [],
    products: [],
    providers: [],
    services: [],
    accounts: [],
  },
  formResponseStepsInvalid: {
    step1: true,
    step2: true,
    step3: true,
    step4: true,
    step5: true,
  },
  mappedQuoteProductCodes: null, // Store mapped product codes for faster retrieval of benefit quote and claim details
};

export default {
  namespaced: true,

  state: cloneDeep(INITIAL_STATE),

  mutations: {
    resetFormResponses(state) {
      // reset everything except step 1 as requirement
      state.formResponse = { ...cloneDeep(INITIAL_STATE.formResponse), step1: state.formResponse.step1 };

      // reference data for providers and services are reset too as get reference apis for these two need to be called again
      state.ref = {
        ...state.ref,
        providers: cloneDeep(INITIAL_STATE.ref.providers),
        services: cloneDeep(INITIAL_STATE.ref.services),
      };

      // set every invalid states to be true
      const formResponseStepsInvalid = state.formResponseStepsInvalid;
      Object.keys(formResponseStepsInvalid).forEach((key) => {
        formResponseStepsInvalid[key] = true;
      });
    },

    resetState(state) {
      Object.assign(state, cloneDeep(INITIAL_STATE));
    },

    resetRefGeneral(state) {
      state.ref.content = cloneDeep(INITIAL_STATE.ref.content);
    },

    updateCurrentPage(state, data) {
      state.currentPage = data;
      if (Number(getQueryString('step')) !== Number(data)) {
        setQueryString('step', data);
      }
    },

    updateRefMemberNumber(state, data) {
      state.ref.memberNumber = data;
    },

    updateRefMembers(state, data) {
      state.ref.members = data;
    },

    updateRefProducts(state, data) {
      state.ref.products = data;
    },

    updateRefProviders(state, data) {
      state.ref.providers = data;
    },

    updateRefServices(state, data) {
      state.ref.services = data;
    },

    updateRefGeneral(state, data) {
      state.ref.content = data;
    },

    updateRefAccounts(state, data) {
      state.ref.accounts = data;
    },

    updateFormResponse(state, data) {
      state.formResponse = data;
    },

    updateFormResponseStepsInvalid(state, data) {
      state.formResponseStepsInvalid = { ...state.formResponseStepsInvalid, ...data };
    },

    updateFileUpload(state, data) {
      state.formResponse.step4.fileUploaded = data;
    },

    updateFileRemoved(state, data) {
      state.formResponse.step4.fileRemoved = data;
    },

    updateMappedQuoteProductCodes(state, data) {
      state.mappedQuoteProductCodes = data;
    }
  },

  getters: {
    getRefData: (state) => {
      return state.ref;
    },

    getFormResponse: (state) => {
      return state.formResponse;
    },

    getCurrentPage: (state) => {
      return state.currentPage;
    },

    getFormResponseStepsInvalid: (state) => {
      return state.formResponseStepsInvalid;
    },

    getSelectedProviderFlexCode: (state) => {
      const [providerId, subProviderIdentifierType] = state.formResponse.step3.selectedProvider.split('-');

      let selectedProvider;

      if (!isEmptyString(subProviderIdentifierType)) {
        selectedProvider = state.ref.providers.find((provider) => {
          return provider.providerId === providerId && provider.subProviderIdentifierType === subProviderIdentifierType;
        });
      } else {
        selectedProvider = state.ref.providers.find((provider) => {
          return provider.providerId === state.formResponse.step3.selectedProvider;
        });
      }

      return selectedProvider !== undefined ? selectedProvider.flexCode : undefined;
    },

    getFileUploadStatus: (state) => {
      return state.formResponse.step4.fileUploaded;
    },

    getFileRemovedStatus: (state) => {
      return state.formResponse.step4.fileRemoved;
    },

    getMappedQuoteProductCodes: (state) => {
      return state.mappedQuoteProductCodes;
    },
  },
};
